import { Flex, Text } from '@chakra-ui/react';
import { faEdit } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Modal,
  useToast
} from '@morressier/alembic';
import { ISubmissionContext } from '@publishing-engine/domain/models';
import { FormProvider, RegisterOptions, useForm } from 'react-hook-form';
import { useUpdateJournalSettings } from 'src/api/mutations';
import GeneralInfoFormField from './GeneralInfoFormField';
import {
  GeneralInfoEditFormValues,
  GeneralInfoEditableFieldConfig,
  validateWhitespace
} from './generalInfoConfig';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  journal: ISubmissionContext;
  fieldConfig: GeneralInfoEditableFieldConfig;
}

const buildRegisterOptions = (
  fieldConfig: GeneralInfoEditableFieldConfig,
  journal: ISubmissionContext
): RegisterOptions<GeneralInfoEditFormValues, 'value'> => {
  const isRequired = fieldConfig.isRequired ?? true;

  return {
    ...(fieldConfig.maxLength
      ? {
          maxLength: {
            value: fieldConfig.maxLength,
            message: `${fieldConfig.label} cannot exceed ${fieldConfig.maxLength} characters`
          }
        }
      : {}),
    validate: value => {
      const whitespaceValidation = validateWhitespace(
        value,
        fieldConfig.label,
        isRequired
      );
      if (whitespaceValidation !== true) {
        return whitespaceValidation;
      }

      if (value.length > 0 && fieldConfig.validate) {
        return fieldConfig.validate(value, journal);
      }

      return true;
    }
  };
};

export const EditGeneralInfo: React.FC<Props> = ({
  journal,
  isOpen,
  onClose,
  fieldConfig
}) => {
  const toast = useToast();
  const updateSettingMutation = useUpdateJournalSettings(journal.id);

  const methods = useForm<GeneralInfoEditFormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      value: fieldConfig.getValue(journal)
    }
  });

  const { handleSubmit, reset } = methods;

  const registerOptions = buildRegisterOptions(fieldConfig, journal);

  const handleClose = () => {
    reset({
      value: fieldConfig.getValue(journal)
    });
    onClose();
  };

  const onSubmit = async ({ value }: GeneralInfoEditFormValues) => {
    const normalizedValue =
      fieldConfig.inputType === 'radio' ? value : value.trim();
    const payload = fieldConfig.buildPayload(normalizedValue, journal);

    const res = await updateSettingMutation.mutateAsync(payload);

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: `${fieldConfig.label} Edited`,
        isClosable: true
      });
      onClose();
      return;
    }

    toast({
      variant: 'error',
      title:
        typeof res?.message === 'string'
          ? res?.message
          : 'Something went wrong',
      isClosable: true
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      width={{ base: 'calc(100vw - 16px)', md: '42rem' } as any}
      closeButtonProps={{
        hidden: true
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex flexDirection={'column'} gap={4} padding={{ base: 4, md: 8 }}>
            <Flex fontSize={32}>
              <FontAwesomeIcon icon={faEdit} />
            </Flex>
            <Text textStyle="headingGroup">{fieldConfig.modalTitle}</Text>

            <GeneralInfoFormField
              fieldConfig={fieldConfig}
              registerOptions={registerOptions}
            />

            <Flex
              justifyContent="flex-end"
              gap={2}
              alignItems="center"
              marginTop={8}
            >
              <Button
                variant="secondary"
                onClick={handleClose}
                isDisabled={updateSettingMutation.isLoading}
              >
                Exit Without Saving
              </Button>
              <Button
                variant="primary"
                type="submit"
                isLoading={updateSettingMutation.isLoading}
              >
                Save Changes
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};
